

import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NeuraSites AI",
    "description": "Professional AI-driven website development for startups and small businesses",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-208-809-3100",
      "contactType": "customer service",
      "email": "hello@neurasites.ai"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2018 N 8th St, Unit B",
      "addressLocality": "Boise",
      "addressRegion": "ID",
      "postalCode": "83702",
      "addressCountry": "US"
    },
    "sameAs": [
      "https://twitter.com/neurasitesai",
      "https://linkedin.com/company/neurasites-ai"
    ],
    "offers": {
      "@type": "Offer",
      "name": "AI-Powered Website Development",
      "description": "Custom website development starting at $999",
      "price": "999",
      "priceCurrency": "USD"
    }
  };

  const services = [
    {
      icon: 'ri-code-s-slash-line',
      title: 'Custom Website Development',
      description: 'AI-powered custom websites tailored to your business needs',
      price: 'Starting at $999'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'E-commerce Solutions',
      description: 'Complete online stores with payment integration',
      price: 'Starting at $1,499'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'SEO Optimization',
      description: 'Get found on Google with our SEO expertise',
      price: 'Starting at $499'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      rating: 5,
      text: 'NeuraSites AI delivered our website in just 2 days! The quality exceeded our expectations.',
      image: '/site-images/sarah-johnson.webp'
    },
    {
      name: 'Michael Chen',
      company: 'GreenLeaf Consulting',
      rating: 5,
      text: 'Incredible value for money. Our new website has increased leads by 300%.',
      image: '/site-images/michael-chen.webp'
    },
    {
      name: 'Emily Rodriguez',
      company: 'Bloom Wellness',
      rating: 5,
      text: 'The AI-powered approach made the whole process seamless and efficient.',
      image: '/site-images/emily-rodriguez.webp'
    }
  ];

  const portfolioHighlights = [
    {
      title: 'TechStart SaaS Platform',
      category: 'SaaS Website',
      image: '/site-images/saas-development-hero.webp'
    },
    {
      title: 'GreenLeaf E-commerce',
      category: 'E-commerce',
      image: '/site-images/e-commerce-hero.webp'
    },
    {
      title: 'Bloom Wellness Center',
      category: 'Healthcare',
      image: '/site-images/website-development-hero.webp'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="NeuraSites AI - AI-Powered Website Development Starting at $999"
        description="Professional AI-driven website development for startups and small businesses. High-quality websites starting at $999, delivered in hours not weeks."
        keywords="AI website development, web design, website builder, startup websites, small business websites, responsive design, SEO optimization, Boise web development"
        ogTitle="NeuraSites AI - Transform Your Business with AI-Powered Websites"
        ogDescription="Get a professional, high-converting website built with AI in hours, not weeks. Perfect for startups and small businesses."
        canonicalUrl={siteUrl}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center brand-soft-surface"
        style={{
          backgroundImage: `url('/site-images/homepage-network.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 brand-soft-overlay"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              AI-Powered Websites
              <span className="block brand-heading-accent">
                Starting at $999
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Transform your business with lightning-fast, AI-driven website development. Professional results in days, not months. 15 years of expertise meets cutting-edge AI technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/vibe-check"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
              >
                Start Your Project
              </Link>
              <Link
                to="/portfolio"
                className="border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-8 py-4 rounded-lg text-lg font-semibold hover:border-blue-600 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400 transition-all whitespace-nowrap"
              >
                View Portfolio
              </Link>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center">
                <i className="ri-time-line text-blue-600 dark:text-blue-400 mr-2"></i>
                Delivered in 24-48 hours
              </div>
              <div className="flex items-center">
                <i className="ri-shield-check-line text-green-600 dark:text-green-400 mr-2"></i>
                15 years experience
              </div>
              <div className="flex items-center">
                <i className="ri-money-dollar-circle-line text-sky-600 dark:text-sky-300 mr-2"></i>
                Starting at $999
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Our AI-Powered Services</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Leveraging cutting-edge AI technology to deliver exceptional websites faster and more affordably than traditional development.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2">
                <div className="w-16 h-16 brand-icon-tile rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{service.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{service.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{service.price}</span>
                  <Link
                    to="/services"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold flex items-center"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Highlights */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Recent Success Stories</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              See how we've helped businesses transform their online presence with AI-powered websites.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {portfolioHighlights.map((project, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-xl shadow-lg">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="text-sm font-medium">{project.category}</p>
                      <h3 className="text-lg font-bold">{project.title}</h3>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/portfolio"
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Real feedback from real businesses we've helped grow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 brand-cta-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl brand-cta-copy mb-8">
            Join hundreds of satisfied clients who've accelerated their growth with AI-powered websites. Get started today and see results in days, not months.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/vibe-check"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Get Started Today
            </Link>
            <a
              href="tel:208-809-3100"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#0F2A4E] transition-colors whitespace-nowrap"
            >
              Call (208) 809-3100
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white"><div className="flex items-center justify-center space-x-3"><i className="ri-time-line text-2xl"></i><span className="text-lg">Fast Delivery</span></div><div className="flex items-center justify-center space-x-3"><i className="ri-shield-check-line text-2xl"></i><span className="text-lg">100% Satisfaction</span></div><div className="flex items-center justify-center space-x-3"><i className="ri-customer-service-2-line text-2xl"></i><span className="text-lg">24/7 Support</span></div></div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
